// ResizableBar.jsx
import React, { useState, useRef, useEffect } from "react";
import dayjs from "dayjs";
import { WarningOutlined } from '@ant-design/icons';
import LinkedItemsPopover from "../Board/LinkedItemPopover";
const MS_PER_DAY = 86400000;
 
export default function ResizableBar({
  start,            // number (ms) or null
  end,              // number (ms) or null
  allStartMs,       // number (ms) - numeric version of parent's allStart
  PIXELS_PER_DAY,   // number - must come from parent (same as header)
  MS_PER_PIXEL,     // number - parent should pass MS_PER_DAY / PIXELS_PER_DAY
  onUpdate,         // fn(newStartMs, newEndMs)
  color = "#b45309",
  label = "",
  scale,             // kept for parity; not used for math
  isOverlapping = false,
  isExpanded = false,
  onExpandClick = () => { },
  bar_name,
  onHandleClick = () => { },
  epic_start,
  epic_end,
  details
 
}) {
 
 
 
  // Local buffered state in ms
  const [localStart, setLocalStart] = useState(start ?? allStartMs);
  const [localEnd, setLocalEnd] = useState(end ?? (allStartMs + MS_PER_DAY));
  const [dragInfo, setDragInfo] = useState(null);
  const [hover, setHover] = useState(false);
 
 
  // sync when parent props change (but not while dragging)
  const draggingRef = useRef(false);
  useEffect(() => {
    if (!draggingRef.current) {
      if (typeof start === "number") setLocalStart(start);
      if (typeof end === "number") setLocalEnd(end);
    }
  }, [start, end]);
 
  useEffect(() => {
    const handleUp = () => {
      setTimeout(() => {
        isDraggingRef.current = false;
      }, 0);
    };
 
    window.addEventListener("mouseup", handleUp);
    window.addEventListener("touchend", handleUp);
 
    return () => {
      window.removeEventListener("mouseup", handleUp);
      window.removeEventListener("touchend", handleUp);
    };
  }, []);
 
  const isDraggingRef = React.useRef(false);
 
  // drag metadata
  const dragMeta = useRef({
    side: null, // "left" | "right" | "move"
    startClientX: 0,
    initialStart: localStart,
    initialEnd: localEnd,
  });
 
  if (start === null || end === null) {
    return null;
  }
  // Safety: ensure numeric inputs exist
  if (typeof allStartMs !== "number" || !PIXELS_PER_DAY || !MS_PER_PIXEL) {
    return null;
  }
  // helpers (consistent with parent)
  const toLeftPx = (timeMs) => ((timeMs - allStartMs) / MS_PER_DAY) * PIXELS_PER_DAY;
  const widthFromTimesToPx = (s, e) => Math.max(((e - s) / MS_PER_DAY) * PIXELS_PER_DAY, PIXELS_PER_DAY);
 
  const leftPx = toLeftPx(localStart);
  const widthPx = widthFromTimesToPx(localStart, localEnd);
 
 
 
  // pointer handlers (mouse + touch)
  const onPointerDown = (e, side) => {
    e.preventDefault();
    e.stopPropagation();
    isDraggingRef.current = true;
 
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
 
    draggingRef.current = true;
    dragMeta.current = {
      side,
      startClientX: clientX,
      initialStart: start ?? localStart,
      initialEnd: end ?? localEnd,
    };
 
    window.addEventListener("mousemove", onPointerMove, { passive: false });
    window.addEventListener("mouseup", onPointerUp);
    window.addEventListener("touchmove", onPointerMove, { passive: false });
    window.addEventListener("touchend", onPointerUp);
  };
 
  const onPointerMove = (ev) => {
    if (!draggingRef.current) return;
    ev.preventDefault();
 
    const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX;
    const dx = clientX - dragMeta.current.startClientX;
 
    const effectiveMS_PER_PIXEL = MS_PER_PIXEL || (MS_PER_DAY / PIXELS_PER_DAY);
    const deltaMs = Math.round(dx * effectiveMS_PER_PIXEL);
 
    let newStart = dragMeta.current.initialStart;
    let newEnd = dragMeta.current.initialEnd;
 
    if (dragMeta.current.side === "move") {
      newStart += deltaMs;
      newEnd += deltaMs;
    } else if (dragMeta.current.side === "left") {
      newStart += deltaMs;
      if (newStart > newEnd - MS_PER_DAY) newStart = newEnd - MS_PER_DAY;
    } else if (dragMeta.current.side === "right") {
      newEnd += deltaMs;
      if (newEnd < newStart + MS_PER_DAY) newEnd = newStart + MS_PER_DAY;
    }
 
    // 🔥 Always store updated values here
    dragMeta.current.updatedStart = newStart;
    dragMeta.current.updatedEnd = newEnd;
 
    setLocalStart(newStart);
    setLocalEnd(newEnd);
 
    const days = Math.max(1, Math.round((newEnd - newStart) / MS_PER_DAY));
    setDragInfo({
      startDate: dayjs(newStart).format("DD MMM, YYYY"),
      endDate: dayjs(newEnd).format("DD MMM, YYYY"),
      days,
    });
  };
 
  const onPointerUp = () => {
    if (!draggingRef.current) return;
 
    draggingRef.current = false;
 
    window.removeEventListener("mousemove", onPointerMove);
    window.removeEventListener("mouseup", onPointerUp);
    window.removeEventListener("touchmove", onPointerMove);
    window.removeEventListener("touchend", onPointerUp);
 
    const finalStart = dragMeta.current.updatedStart ?? localStart;
    const finalEnd = dragMeta.current.updatedEnd ?? localEnd;
 
    setDragInfo(null);
 
    onUpdate?.(finalStart, finalEnd);
  };
 
  if (bar_name === "sprint-bar") {
    return (
      <div
        role="button"
        aria-label={label}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        title={`${label} • ${dayjs(localStart).format("DD MMM, YYYY")} → ${dayjs(localEnd).format("DD MMM, YYYY")}`}
        style={{
          position: "absolute",
          left: Math.round(leftPx),
          width: Math.round(widthPx),
          height: 20,
          backgroundColor: "white",
          borderRadius: 4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "black",
          fontSize: 12,
          userSelect: "none",
          zIndex: hover ? 100 : 10,
          border: "1px solid grey",
          boxSizing: "border-box",
          padding: "0 8px",
          transition: "box-shadow 0.15s ease, border 0.15s ease"
        }}
      >
        {/* left handle */}
        <div
          onMouseDown={(e) => onPointerDown(e, "left")}
          onTouchStart={(e) => onPointerDown(e, "left")}
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: 12,
            height: "100%",
            cursor: "ew-resize",
            zIndex: 20,
          }}
        />
 
        <div
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontWeight: 500,
            pointerEvents: "none",
            width: "100%",
            textAlign: "center",
          }}
        >
          {label}
        </div>
 
        {/* right handle */}
        <div
          onMouseDown={(e) => onPointerDown(e, "right")}
          onTouchStart={(e) => onPointerDown(e, "right")}
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            width: 12,
            height: "100%",
            cursor: "ew-resize",
            zIndex: 20,
          }}
        />
 
        {/* live tooltip */}
        {dragInfo && (
          <>
            <div
              style={{
                position: "absolute",
                top: -26,
                left: 4,
                background: "#111",
                color: "#fff",
                padding: "4px 8px",
                fontSize: 12,
                borderRadius: 4,
                whiteSpace: "nowrap",
                zIndex: 30,
              }}
            >
              {dragInfo.startDate}
            </div>
            <div
              style={{
                position: "absolute",
                top: -26,
                right: 4,
                background: "#111",
                color: "#fff",
                padding: "4px 8px",
                fontSize: 12,
                borderRadius: 4,
                whiteSpace: "nowrap",
                zIndex: 30,
              }}
            >
              {dragInfo.endDate} ({dragInfo.days} day{dragInfo.days > 1 ? "s" : ""})
            </div>
          </>
        )}
      </div>
    );
  }
 
 
  // normal expanded bar
  return (
    <div>
      <div
        role="button"
        aria-label={label}
        title={`${label} • ${dayjs(localStart).format("DD MMM, YYYY")} → ${dayjs(localEnd).format("DD MMM, YYYY")}`}
        onClick={(e) => {
          if (isDraggingRef.current) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          onHandleClick();
        }}
        style={{
          position: "relative",
          left: Math.round(leftPx),
          width: Math.round(widthPx),
          height:'24px',
          backgroundColor: color,
          borderRadius: 6,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          fontSize: 12,
          userSelect: "none",
          zIndex: 10,
          boxSizing: "border-box",
          padding: "0px 8px",
        }}
      >
        {/* left handle */}
        <div
          onMouseDown={(e) => onPointerDown(e, "left")}
          onTouchStart={(e) => onPointerDown(e, "left")}
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: 12,
            height: "100%",
            cursor: "ew-resize",
            zIndex: 20,
          }}
        />
 
        <div
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontWeight: 600,
            pointerEvents: "none",
            width: "100%",
            textAlign: "center",
          }}
        >
          {label}
        </div>
 
        {/* right handle */}
        <div
          onMouseDown={(e) => onPointerDown(e, "right")}
          onTouchStart={(e) => onPointerDown(e, "right")}
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            width: 12,
            height: "100%",
            cursor: "ew-resize",
            zIndex: 20,
          }}
        />
 
        {/* live tooltip */}
        {dragInfo && (
          <>
            <div
              style={{
                position: "absolute",
                top: -26,
                left: 4,
                background: "#111",
                color: "#fff",
                padding: "4px 8px",
                fontSize: 12,
                borderRadius: 4,
                whiteSpace: "nowrap",
                zIndex: 30,
              }}
            >
              {dragInfo.startDate}
            </div>
            <div
              style={{
                position: "absolute",
                top: -26,
                right: 4,
                background: "#111",
                color: "#fff",
                padding: "4px 8px",
                fontSize: 12,
                borderRadius: 4,
                whiteSpace: "nowrap",
                zIndex: 30,
              }}
            >
              {dragInfo.endDate} ({dragInfo.days} day{dragInfo.days > 1 ? "s" : ""})
            </div>
          </>
        )}
      </div>
      {/* Linked Items Popover at END of bar */}
<div
  style={{
    position: "absolute",
    left: Math.round(leftPx) + Math.round(widthPx) + 8,
    top: bar_name === "sprint-bar" ? 0 : 5,
    zIndex: 100,
  }}
>
  <LinkedItemsPopover
    item_id={details?.issue_id ||details?.epic_id}
    item_type={bar_name === "epic-bar" ? "epic" : "issue"}
    details={details}
  />
</div>
      {bar_name === 'task-bar' && start && end && epic_start && epic_end && (start < epic_start || end > epic_end) && <WarningOutlined style={{ position: "absolute", left: (Math.round(leftPx) + Math.round(widthPx) + 5), fontSize: 18, top: 5, color: '#FA8C16', zIndex: 100 }} title="The parent work item’s end date occurs before the child work item’s scheduled completion." />}
    </div>
  );
}